// Vocabulary pillar — a calm, themed word-card browser.
//
// Same hybrid flow as the verb browser: public `vocabulary` (guest-readable) →
// overlay user_workspace (pins/notes) → render. Cards are grouped by PCIC-style
// theme, filterable by theme chip + CEFR tier, with emoji dual-coding and a
// worked example sentence. No scores, no streaks — just a reference you browse.
import { fetchVocabulary, fetchWorkspace, overlay } from "../lib/data.js";
import { VOCAB_THEMES, VOCAB_TIERS } from "../data/vocabulary.js";
import { renderNotesPin } from "../ui/notesPin.js";
import { openAuthPanel } from "../ui/authPanel.js";
import { escapeHtml, highlightMatch } from "../ui/helpers.js";
import { speakButtonHTML } from "../lib/audio.js";

let cache = null; // { cards: overlaid[] }

async function loadData() {
  const [cards, workspace] = await Promise.all([fetchVocabulary(), fetchWorkspace()]);
  cache = { cards: overlay(cards, workspace, "vocabulary") };
  return cache;
}

const THEME_LABEL = Object.fromEntries(VOCAB_THEMES.map((t) => [t.id, t.label]));
const THEME_EMOJI = Object.fromEntries(VOCAB_THEMES.map((t) => [t.id, t.emoji]));

function posBadge(pos) {
  const map = {
    noun: "bg-sage-50 text-sage-600 border-sage-100",
    verb: "bg-amber-50 text-amber-700 border-amber-100",
    adjective: "bg-sky-50 text-sky-700 border-sky-100",
    adverb: "bg-violet-50 text-violet-700 border-violet-100",
    phrase: "bg-rose-50 text-rose-700 border-rose-100",
  };
  return map[pos] || "bg-sage-50 text-sage-600 border-sage-100";
}

// Gender tints only matter for nouns; everything else shows nothing.
function genderTag(gender) {
  if (gender === "m") return `<span class="text-[10px] text-blue-500 font-mono">o</span>`;
  if (gender === "f") return `<span class="text-[10px] text-rose-500 font-mono">a</span>`;
  if (gender === "mf") return `<span class="text-[10px] text-sage-500 font-mono">o/a</span>`;
  return "";
}

export async function vocabularyView(app, params = {}) {
  app.innerHTML = `<div class="py-16 text-center text-sage-400 text-sm">Loading vocabulary…</div>`;
  const data = await loadData();
  const total = data.cards.length;

  app.innerHTML = `
    <section>
      <div class="mb-6">
        <h2 class="text-2xl font-bold text-sage-800">Vocabulary</h2>
        <p class="text-sage-600 text-sm mt-1">${total} everyday words, grouped by theme and CEFR tier. Each card pairs the word with a picture cue and a sentence in context.</p>
      </div>
      <div class="relative mb-3">
        <div class="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400 pointer-events-none">🔍</div>
        <input id="vocab-search" type="text" placeholder="Try 'house' or 'casa'…"
          class="w-full pl-11 pr-4 py-3 rounded-xl border border-sage-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none text-base" />
      </div>
      <div class="flex flex-wrap items-center gap-1.5 mb-3" id="theme-chips">
        <button class="theme-chip" data-theme="">All themes</button>
        ${VOCAB_THEMES.map(
          (t) => `<button class="theme-chip" data-theme="${t.id}">${t.emoji} ${escapeHtml(t.label)}</button>`
        ).join("")}
      </div>
      <div class="flex flex-wrap items-center gap-2 mb-4">
        <span class="text-xs text-sage-500 uppercase tracking-wide mr-1">Tier:</span>
        <button class="tier-chip" data-tier="">All</button>
        ${VOCAB_TIERS.map((t) => `<button class="tier-chip" data-tier="${t}">${t}</button>`).join("")}
      </div>
      <p id="vocab-count" class="text-xs text-sage-500 mb-3"></p>
      <div id="vocab-list"></div>
    </section>`;

  const input = app.querySelector("#vocab-search");
  const list = app.querySelector("#vocab-list");
  const count = app.querySelector("#vocab-count");
  let theme = params.theme && THEME_LABEL[params.theme] ? params.theme : "";
  let tier = VOCAB_TIERS.includes(params.tier) ? params.tier : "";

  function paintChips() {
    app.querySelectorAll(".theme-chip").forEach((b) => {
      const on = b.dataset.theme === theme;
      b.className = `theme-chip px-2.5 py-1 rounded-full text-xs font-medium border transition ${
        on ? "bg-sage-500 text-white border-sage-500" : "bg-white text-sage-600 border-sage-200 hover:border-sage-300"
      }`;
    });
    app.querySelectorAll(".tier-chip").forEach((b) => {
      const on = b.dataset.tier === tier;
      b.className = `tier-chip px-3 py-1 rounded-full text-xs font-medium border transition ${
        on ? "bg-sage-500 text-white border-sage-500" : "bg-white text-sage-600 border-sage-200 hover:border-sage-300"
      }`;
    });
  }

  function render() {
    const q = input.value.trim().toLowerCase();
    const results = data.cards.filter((c) => {
      if (theme && c.theme !== theme) return false;
      if (tier && c.tier !== tier) return false;
      if (!q) return true;
      return (
        c.portuguese.toLowerCase().includes(q) ||
        (c.english || "").toLowerCase().includes(q) ||
        (c.example_pt || "").toLowerCase().includes(q)
      );
    });

    count.innerHTML = q
      ? `<b>${results.length}</b> match${results.length === 1 ? "" : "es"} for "<span class='text-sage-700'>${escapeHtml(q)}</span>"`
      : `${results.length} word${results.length === 1 ? "" : "s"}${theme ? " in " + escapeHtml(THEME_LABEL[theme]) : ""}${tier ? " · " + tier : ""}`;

    if (!results.length) {
      list.innerHTML = `<div class="text-center py-10 text-sage-500">
        <p class="text-2xl mb-2">🤔</p><p>No words found${q ? ` for "${escapeHtml(q)}"` : ""}.</p>
        <p class="text-xs mt-2 text-sage-400">Try a different word, drop accents, or clear the filters.</p></div>`;
      return;
    }

    // Group results by theme so the page reads like a themed glossary.
    const order = VOCAB_THEMES.map((t) => t.id);
    const groups = new Map();
    for (const c of results) {
      if (!groups.has(c.theme)) groups.set(c.theme, []);
      groups.get(c.theme).push(c);
    }
    const sortedThemes = [...groups.keys()].sort((a, b) => order.indexOf(a) - order.indexOf(b));

    list.innerHTML = sortedThemes
      .map((th) => {
        const cards = groups.get(th);
        return `
          <div class="mb-6">
            <h3 class="flex items-center gap-2 text-sm font-semibold text-sage-700 mb-2 sticky top-16 bg-white/80 backdrop-blur py-1 z-10">
              <span class="text-base">${THEME_EMOJI[th] || "📚"}</span>${escapeHtml(THEME_LABEL[th] || th)}
              <span class="text-xs font-normal text-sage-400">${cards.length}</span>
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              ${cards.map((c) => cardHTML(c, q)).join("")}
            </div>
          </div>`;
      })
      .join("");

    list.querySelectorAll("[data-card-id]").forEach((el) => {
      el.addEventListener("click", () => {
        const card = data.cards.find((c) => c.id === el.dataset.cardId);
        if (card) showDetail(card);
      });
    });
  }

  function cardHTML(c, q) {
    return `
      <button data-card-id="${escapeHtml(c.id)}"
        class="bg-white border border-sage-100 hover:border-sage-300 rounded-lg p-3 text-left transition flex items-start gap-3">
        <span class="text-2xl leading-none mt-0.5" aria-hidden="true">${c.emoji || "•"}</span>
        <div class="min-w-0 flex-1">
          <p class="font-semibold text-sage-800 truncate flex items-center gap-1.5">
            ${genderTag(c.gender)}${highlightMatch(c.portuguese, q)}${c.is_pinned ? ' <span title="Pinned">📌</span>' : ""}
          </p>
          <p class="text-xs text-sage-500 truncate">${highlightMatch(c.english || "", q)}</p>
        </div>
        <span class="text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap border ${posBadge(c.part_of_speech)}">${c.part_of_speech}</span>
      </button>`;
  }

  input.addEventListener("input", render);
  app.querySelectorAll(".theme-chip").forEach((b) =>
    b.addEventListener("click", () => {
      theme = b.dataset.theme;
      paintChips();
      render();
    })
  );
  app.querySelectorAll(".tier-chip").forEach((b) =>
    b.addEventListener("click", () => {
      tier = b.dataset.tier;
      paintChips();
      render();
    })
  );

  paintChips();
  render();
}

// ---- Word detail modal --------------------------------------------------

function onModalKeydown(e) {
  if (e.key === "Escape") closeDetail();
}

function closeDetail() {
  const existing = document.getElementById("vocab-modal");
  if (existing) existing.remove();
  document.removeEventListener("keydown", onModalKeydown);
}

function showDetail(c) {
  closeDetail();

  const overlay = document.createElement("div");
  overlay.id = "vocab-modal";
  overlay.className =
    "fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 sm:p-6 bg-sage-900/30 backdrop-blur-sm overflow-y-auto fade-in";

  const card = document.createElement("div");
  card.className =
    "bg-white border border-sage-200 rounded-2xl p-5 sm:p-6 shadow-xl w-full max-w-lg my-8 relative";
  card.innerHTML = `
    <button data-close aria-label="Close" class="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-sage-100 text-sage-500 flex items-center justify-center text-lg">✕</button>
    <div class="pr-10 flex items-start gap-4">
      <span class="text-5xl leading-none" aria-hidden="true">${c.emoji || "•"}</span>
      <div class="min-w-0">
        <h3 class="text-2xl font-bold text-sage-800 flex items-center gap-2">${genderTag(c.gender)}${escapeHtml(c.portuguese)}${speakButtonHTML(c.portuguese, { size: "lg" })}</h3>
        <p class="text-sage-500">${escapeHtml(c.english || "")}</p>
        <div class="flex flex-wrap items-center gap-1.5 mt-2">
          <span class="text-[11px] px-2 py-0.5 rounded-full border ${posBadge(c.part_of_speech)}">${c.part_of_speech}</span>
          <span class="text-[11px] px-2 py-0.5 rounded-full border bg-sage-50 text-sage-600 border-sage-100">${THEME_EMOJI[c.theme] || ""} ${escapeHtml(THEME_LABEL[c.theme] || c.theme)}</span>
          <span class="text-[11px] px-2 py-0.5 rounded-full border bg-white text-sage-600 border-sage-200">${escapeHtml(c.tier)}</span>
        </div>
      </div>
    </div>
    ${
      c.example_pt
        ? `<div class="mt-4 rounded-xl border border-sage-100 bg-sand-50/60 p-4">
            <p class="text-sage-800 flex items-start gap-1.5">${escapeHtml(c.example_pt)}${speakButtonHTML(c.example_pt)}</p>
            <p class="text-sm text-sage-500 mt-1 italic">${escapeHtml(c.example_en || "")}</p>
          </div>`
        : ""
    }`;

  card.appendChild(renderNotesPin(c, "vocabulary", () => openAuthPanel("signin")));
  overlay.appendChild(card);
  document.body.appendChild(overlay);

  overlay.addEventListener("mousedown", (e) => {
    if (e.target === overlay) closeDetail();
  });
  card.querySelector("[data-close]").addEventListener("click", closeDetail);
  document.addEventListener("keydown", onModalKeydown);
}
